import { Injectable, NgZone, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject, Subscription, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthEvent, AuthEventService } from 'src/app/user/shared/services/auth-event.service';
import { AuthTokenService } from 'src/app/core/shared/auth-token.service';
import { MatDialog } from '@angular/material/dialog';
import { CameraAlarmPopUpComponent } from '../../project-schema/shared/components/camera-alarm-pop-up/camera-alarm-pop-up.component';
import { CameraViewModalComponent } from '../../project-schema/shared/components/camera-view-modal/camera-view-modal.component';
import { ProjectDataService } from './project-data.service';
import { Router } from '@angular/router';
import { Project } from '../interfaces/project.interface';
import { ProjectGroup } from '../interfaces/project-group.interface';
import { CameraTypeEnum, getCameraName } from '../../shared/enums/camera-type.enum';

export interface AlarmData {
  id?: number; //WarnId
  timestamp?: string;
  guid: string;
  groupId: number;
  projectId: number;
  message: string;
}

// Erweiterte Connection Status Types für bessere UI-Integration
export type ConnectionStatus = 'connected' | 'connecting' | 'reconnecting' | 'disconnected' | 'error';

@Injectable({
  providedIn: 'root'
})
export class AlarmService implements OnDestroy {
  private hubConnection!: signalR.HubConnection;
  private alarmSubject = new Subject<AlarmData>();
  private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>('disconnected');
  private shouldConnect = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  // Public Observables für Component-Integration
  public alarm$ = this.alarmSubject.asObservable();
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  public isAcousticAlarmsActivated: boolean = false;
  public isAlarmsActivated: boolean = false;

  private projects: Project[] = [];
  private projectsSubscription: Subscription;
  private projectGroups: ProjectGroup[] = [];
  private projectGroupsSubscription: Subscription;
  private connectionQueue: Promise<void> = Promise.resolve();
  private readonly alarmAudio = new Audio('assets/alarm/beep-long.mp3');
  private alarmAudioUnlocked = false;
  private alarmAudioUnlockListenersRegistered = false;
  private readonly alarmAudioUnlockHandler = () => this.unlockAlarmAudio();

  constructor(
    private ngZone: NgZone,
    private authEventService: AuthEventService,
    private authTokenService: AuthTokenService,
    private dialog: MatDialog,
    private projectDataService: ProjectDataService,
    private router: Router
  ) {
    this.authEventService.events$.subscribe(event => {
      this.queueAuthEventHandler(event);
    });

    this.projectsSubscription = this.projectDataService.projects.subscribe(projects => {
      this.projects = projects;
    });

    this.projectGroupsSubscription = this.projectDataService.projectGroups.subscribe(groups => {
      this.projectGroups = groups;
    });

    this.prepareAlarmAudio();
  }

  private queueAuthEventHandler(event: AuthEvent): void {
    this.connectionQueue = this.connectionQueue.then(() => {
      return this.handleAuthEvent(event);
    }).catch(error => {
      console.error('Error handling auth event in AlarmService:', error);
    });
  }

  private async handleAuthEvent(event: AuthEvent): Promise<void> {
    this.isAcousticAlarmsActivated = event.userData?.isAcousticAlarmsActivated ?? false;
    this.isAlarmsActivated = event.userData?.isAlarmsActivated ?? false;

    switch (event.type) {
      case 'login':
      case 'user-loaded':
        if (event.userData?.isAuthorizedForAlarms && event.userData?.isAlarmsActivated) {
          await this.connect();
        } else {
          await this.disconnect();
        }
        break;
      case 'logout':
        await this.disconnect();
        break;
    }
  }

  // Public method to connect - kann auch manuell von UI aufgerufen werden
  public async connect(): Promise<void> {
    try {
      await firstValueFrom(this.authTokenService.getValidAccessToken());
    } catch (error) {
      this.connectionStatusSubject.next('error');
      return;
    }

    // Check if already connected
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      return;
    }

    // Ensure clean disconnect first
    if (this.hubConnection) {
      await this.disconnect();
    }

    this.shouldConnect = true;
    this.reconnectAttempts = 0;
    await this.startConnection();
  }
  // Public method to disconnect - kann auch manuell von UI aufgerufen werden
  public async disconnect(): Promise<void> {
    this.shouldConnect = false;
    this.reconnectAttempts = 0;

    if (this.hubConnection) {
      try {
        this.removeEventHandlers();
        await this.hubConnection.stop();
        this.connectionStatusSubject.next('disconnected');
      } catch (err) {
        this.connectionStatusSubject.next('error');
      } finally {
        this.hubConnection = null!;
      }
    } else {
      this.connectionStatusSubject.next('disconnected');
    }
  }

  private async startConnection(): Promise<void> {
    if (!this.shouldConnect) {
      return;
    }

    try {
      await firstValueFrom(this.authTokenService.getValidAccessToken());
    } catch (error) {
      this.connectionStatusSubject.next('error');
      return;
    }

    try {
      this.connectionStatusSubject.next('connecting');
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${environment.apiAddress}hubs/alarms`, {
          accessTokenFactory: () => firstValueFrom(this.authTokenService.getValidAccessToken())
        })
        .withAutomaticReconnect([0, 2000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

      this.registerEventHandlers();

      await this.hubConnection.start();

      this.connectionStatusSubject.next('connected');
      this.reconnectAttempts = 0;

    } catch (err) {
      this.connectionStatusSubject.next('error');

      if (this.shouldConnect && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        const retryDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

        await new Promise(resolve => setTimeout(resolve, retryDelay));
        if (this.shouldConnect) {
          await this.startConnection();
        }
      } else {
        this.connectionStatusSubject.next('disconnected');
      }
    }
  }

  private registerEventHandlers(): void {
    if (!this.hubConnection) return;

    // Alarm-Handler
    this.hubConnection.on('ReceiveAlarm', (data: AlarmData) => {
      this.ngZone.run(() => {

        if (this.isAlarmsActivated) {
          if (this.isAcousticAlarmsActivated) {
            this.playAlarmSound();
          }
          this.openWarningDialog(data);
        }
        this.alarmSubject.next(data);
      });
    });

    // Connection Lifecycle Events für bessere Status-Anzeige
    this.hubConnection.onreconnecting((error) => {
      this.ngZone.run(() => {
        this.connectionStatusSubject.next('reconnecting');
      });
    });

    this.hubConnection.onreconnected((connectionId) => {
      this.ngZone.run(() => {
        this.connectionStatusSubject.next('connected');
        this.reconnectAttempts = 0;
      });
    });

    this.hubConnection.onclose((error) => {
      this.ngZone.run(() => {
        if (error) {
          this.connectionStatusSubject.next('error');

          // Auto-reconnect bei unerwarteter Trennung
          if (this.shouldConnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            setTimeout(() => this.startConnection(), 2000);
          }
        } else {
          // Normale Trennung (disconnect() aufgerufen)
          this.connectionStatusSubject.next('disconnected');
        }
      });
    });
  }

  openWarningDialog(data: AlarmData): void {
    const ref = this.dialog.open(CameraAlarmPopUpComponent, { data });
    ref.afterClosed().subscribe(result => {
      if (result?.action === 'STREAM') {
/*         console.log('STREAM action selected');
 */        const { projectId, groupId, stationGuid } = result;
        // Projekt setzen
        /* projekt über id raussuchen */
        const project = this.projects.find(p => p.id === projectId);
        if (project) {
          this.projectDataService.updateActiveProject(project);
        }

        setTimeout(() => {

          const group = this.projectGroups.find(g => g.id === groupId);
          if (group) {
            this.projectDataService.updateActiveGroup(group);
          }
        }, 550);

        this.router.navigate(['/project/schema']).then(() => {
          setTimeout(() => {

            if (stationGuid) {
              this.dialog.open(CameraViewModalComponent, {
                data: { name: getCameraName(CameraTypeEnum.PTZ), rs: stationGuid, type: CameraTypeEnum.PTZ },
                hasBackdrop: false,
                panelClass: 'draggable-resizable-dialog'

              });
            }
          }, 550);
        });
      }
      if (result?.action === 'RECORDINGS') {
/*         console.log('RECORDINGS action received');
 */        const { projectId, groupId, stationGuid } = result;
        const project = this.projects.find(p => p.id === projectId);
        if (project) {
          this.projectDataService.updateActiveProject(project);
        }
        setTimeout(() => {
          const group = this.projectGroups.find(g => g.id === groupId);
          if (group) {
            this.projectDataService.updateActiveGroup(group);
          }
        }, 550);
        this.router.navigate(['/project/traffic-monitoring']).then(() => { });
      }
      // IGNORE → nichts weiter
    });
  }

  playAlarmSound(): void {
    this.alarmAudio.pause();
    this.alarmAudio.currentTime = 0;
    this.alarmAudio.muted = false;
    this.alarmAudio.volume = 1;

    this.alarmAudio.play()
      .then(() => {
        this.alarmAudioUnlocked = true;
      })
      .catch(err => {
        this.alarmAudioUnlocked = false;
        this.registerAlarmAudioUnlockListeners();
        console.error('Fehler beim Abspielen des Alarmsounds:', err);
      });
  }

  private prepareAlarmAudio(): void {
    this.alarmAudio.preload = 'auto';
    this.alarmAudio.load();
    this.registerAlarmAudioUnlockListeners();
  }

  private registerAlarmAudioUnlockListeners(): void {
    if (this.alarmAudioUnlocked || this.alarmAudioUnlockListenersRegistered) {
      return;
    }

    this.alarmAudioUnlockListenersRegistered = true;
    document.addEventListener('pointerdown', this.alarmAudioUnlockHandler, { once: true, capture: true });
    document.addEventListener('keydown', this.alarmAudioUnlockHandler, { once: true, capture: true });
    document.addEventListener('touchstart', this.alarmAudioUnlockHandler, { once: true, capture: true });
  }

  private removeAlarmAudioUnlockListeners(): void {
    document.removeEventListener('pointerdown', this.alarmAudioUnlockHandler, true);
    document.removeEventListener('keydown', this.alarmAudioUnlockHandler, true);
    document.removeEventListener('touchstart', this.alarmAudioUnlockHandler, true);
    this.alarmAudioUnlockListenersRegistered = false;
  }

  private unlockAlarmAudio(): void {
    this.removeAlarmAudioUnlockListeners();

    const previousVolume = this.alarmAudio.volume;
    const wasMuted = this.alarmAudio.muted;

    this.alarmAudio.volume = 0;
    this.alarmAudio.muted = true;

    this.alarmAudio.play()
      .then(() => {
        this.alarmAudio.pause();
        this.alarmAudio.currentTime = 0;
        this.alarmAudio.volume = previousVolume;
        this.alarmAudio.muted = wasMuted;
        this.alarmAudioUnlocked = true;
      })
      .catch(() => {
        this.alarmAudio.pause();
        this.alarmAudio.currentTime = 0;
        this.alarmAudio.volume = previousVolume;
        this.alarmAudio.muted = wasMuted;
        this.alarmAudioUnlocked = false;
        this.registerAlarmAudioUnlockListeners();
      });
  }

  private removeEventHandlers(): void {
    if (!this.hubConnection) return;

    try {
      this.hubConnection.off('ReceiveAlarm');
    } catch (error) {
    }
  }

  // Test-Alarm senden (für Debugging/Testing)
  public sendTestAlarm(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
        this.hubConnection
          .invoke('SendAlarm', id)
          .then(() => {
            resolve();
          })
          .catch(err => {
            reject(err);
          });
      } else {
        const error = 'Cannot send alarm: SignalR not connected';
        reject(new Error(error));
      }
    });
  }

  // Getter für aktuellen Verbindungsstatus (für synchrone Abfragen)
  public get isConnected(): boolean {
    return this.connectionStatusSubject.value === 'connected';
  }

  public get connectionStatus(): ConnectionStatus {
    return this.connectionStatusSubject.value;
  }

  // Connection State für erweiterte Debugging-Informationen
  public getConnectionState(): signalR.HubConnectionState | undefined {
    return this.hubConnection?.state;
  }

  // Detaillierte Connection-Info für Status-Anzeige
  public getConnectionInfo(): {
    status: ConnectionStatus;
    signalRState?: signalR.HubConnectionState;
    shouldConnect: boolean;
    reconnectAttempts: number;
  } {
    return {
      status: this.connectionStatus,
      signalRState: this.getConnectionState(),
      shouldConnect: this.shouldConnect,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  // Manually force reconnection (für UI-Button)
  public async forceReconnect(): Promise<void> {
    const reconnectAfterDisconnect = this.shouldConnect;

    await this.disconnect();

    if (reconnectAfterDisconnect) {
      await this.connect();
    }
  }

  // Service-Cleanup bei App-Beendigung
  public ngOnDestroy(): void {
    this.removeAlarmAudioUnlockListeners();
    this.disconnect();
    this.alarmSubject.complete();
    this.connectionStatusSubject.complete();
  }
}
